import {
  Card,
  styled,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Button,
  Modal,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import VisitedMonitored from "../specials/VisitedMonitored";
import AdminMonitored from "../specials/AdminMonitored";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonIcon from "@mui/icons-material/Person";
import { useState, useEffect } from "react";
import { ContentTabStyleCard, TabstyledCard } from "../specials/StylesCards";
import DataGridSSR from "../../components/moleculas/datagrids";
import api from "../../services/axios";
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import {
  IMeta,
  IPagination,
  User,
} from "../../store/schemas/monitoring.schema";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useRoleCheck } from "../../hooks/useRoleCheck";

dayjs.extend(utc);
dayjs.extend(timezone);

const StyledCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: 300,
  height: "51.6rem",
  marginBottom: "1rem",
  padding: theme.spacing(0, 0, 0, 0),
  boxShadow: theme.shadows[3],
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

const GeneralTabContent = () => {
  const [subTab, setSubTab] = useState(0);
  return (
    <ContentTabStyleCard>
      <TabstyledCard>
        <Tabs
          value={subTab}
          onChange={(_, newValue) => setSubTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Historial Visitantes QR" />
          <Tab label="Historial Administrador" />
        </Tabs>
        <Box
          sx={{
            p: 3,
            width: "100%",
            height: "calc(75vh - 50px)", // Altura fija menos espacio de tabs
            overflow: "auto", // Scroll cuando el contenido excede
          }}
        >
          {subTab === 0 && <VisitedMonitored />}
          {subTab === 1 && <AdminMonitored />}
        </Box>
      </TabstyledCard>
    </ContentTabStyleCard>
  );
};

const UserTabContent = () => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [allData, setAllData] = useState<User[]>([]);

  // Estados para modal de edición
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    password: "",
    confirmPassword: "",
  });
  const [editErrorMessage, setEditErrorMessage] = useState<string | null>(null);

  const handleOpenDeleteModal = (user: User) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedUser(null);
  };

  // Funciones para modal de edición
  const handleOpenEditModal = (user: User) => {
    setSelectedUser(user);
    setEditData({
      name: user.name || "",
      password: "",
      confirmPassword: "",
    });
    setEditErrorMessage(null);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditData({ name: "", password: "", confirmPassword: "" });
    setEditErrorMessage(null);
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
    if (editErrorMessage) setEditErrorMessage(null);
  };

  const handleConfirmEdit = async () => {
    if (!selectedUser) return;

    // Validar contraseñas si se quiere cambiar
    if (editData.password || editData.confirmPassword) {
      if (editData.password !== editData.confirmPassword) {
        setEditErrorMessage("Las contraseñas no coinciden");
        return;
      }
      if (editData.password.length < 6) {
        setEditErrorMessage("La contraseña debe tener al menos 6 caracteres");
        return;
      }
    }

    try {
      setEditLoading(true);
      setEditErrorMessage(null);

      const updateData: { name?: string; password?: string } = {};

      if (editData.name && editData.name !== selectedUser.name) {
        updateData.name = editData.name;
      }

      if (editData.password) {
        updateData.password = editData.password;
      }

      if (Object.keys(updateData).length === 0) {
        setEditErrorMessage("No hay cambios para guardar");
        return;
      }

      await api.put(`/settings-app/admins/${selectedUser.id}`, updateData);

      await fetchUsers();
      handleCloseEditModal();
      alert("Usuario actualizado exitosamente");
    } catch (error: unknown) {
      let errorMessage = "Error al actualizar usuario";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = apiError.response?.data?.message || errorMessage;
      }
      setEditErrorMessage(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      setDeleteLoading(true);
      await api.delete(`/settings-app/delete-admins/${selectedUser.id}`);

      await fetchUsers();
      handleCloseDeleteModal();
      alert("Usuario eliminado exitosamente");
    } catch (error: unknown) {
      let errorMessage = "Error al eliminar usuario";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = apiError.response?.data?.message || errorMessage;
      }
      console.error("Error al eliminar usuario:", error);
      alert(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const [openModal, setOpenModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleOpenModal = () => {
    setOpenModal(true);
    setErrorMessage(null); // Limpiar mensajes de error al abrir el modal
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewUser({ name: "", email: "", password: "" });
    setErrorMessage(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
    // Limpiar mensajes de error al editar
    if (errorMessage) setErrorMessage(null);
  };

  const handleCreateUser = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const userData = {
        name: newUser.name,
        credentials: {
          email: newUser.email,
          password: newUser.password,
        },
      };

      await api.post("/auth/register", userData);

      // Refrescar los datos y resetear
      await fetchUsers();
      handleCloseModal();
      alert("Usuario creado exitosamente");
    } catch (error: unknown) {
      let errorMessage = "Error al crear el usuario";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = apiError.response?.data?.message || errorMessage;
      }
      setErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const [pagination, setPagination] = useState<IPagination>({
    page: 0,
    limit: 5,
  });
  const [meta, setMeta] = useState<IMeta>({
    totalItems: 0,
    itemCount: 0,
    itemsPerPage: 0,
    totalPages: 0,
    currentPage: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchUsers = async (searchTerm: string = "") => {
    setLoading(true);
    try {
      const encodedSearch = encodeURIComponent(searchTerm);
      const url = `/settings-app/admins?page=${pagination.page + 1}&limit=${pagination.limit}${encodedSearch ? `&search=${encodedSearch}` : ""}`;


      const response = await api.get(url);

      const items = response.data.items || [];
      const meta = response.data.meta || {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: pagination.limit,
        totalPages: 0,
        currentPage: pagination.page + 1,
      };

      setAllData(items);
      setMeta({
        totalItems: meta.totalItems,
        itemCount: meta.itemCount,
        itemsPerPage: meta.itemsPerPage,
        totalPages: meta.totalPages,
        currentPage: meta.currentPage - 1,
      });
    } catch (error: unknown) {
      console.error("Error al cargar usuarios:");

      if (error instanceof Error) {
        console.error("Mensaje de error:", error.message);
        if ("response" in error) {
          const apiError = error as { response?: any };
          console.error("Detalles del error:", {
            status: apiError.response?.status,
            data: apiError.response?.data,
            headers: apiError.response?.headers,
          });
        }
      } else {
        console.error("Error desconocido:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Usar debounce para evitar múltiples llamadas al API
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchUsers(searchTerm);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [pagination.page, pagination.limit, searchTerm]);

  const handlePaginationChange = (model: GridPaginationModel) => {
    // Actualizar solo cuando realmente cambie
    if (model.page !== pagination.page || model.pageSize !== pagination.limit) {
      setPagination({
        page: model.page,
        limit: model.pageSize,
      });
    }
  };

  const columns: GridColDef<User>[] = [
    { field: "id", headerName: "ID", flex: 0.3 },
    { field: "name", headerName: "Nombre", flex: 0.5 },
    {
      field: "email",
      headerName: "Email",
      flex: 0.7,
      renderCell: (params: GridRenderCellParams<User>) =>
        params.row.credential?.email || "N/A",
    },
    //
    {
      field: "created_at",
      headerName: "Fecha de Creación",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<User>) =>
        dayjs(params.row.created_at).format("DD/MM/YYYY"),
    },
    {
      field: "updated_at",
      headerName: "Última Actualización",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams<User>) =>
        dayjs(params.row.updated_at).format("DD/MM/YYYY"), // Aquí corregí el error
    },
  ];

  return (
    <ContentTabStyleCard>
      <TabstyledCard>
        <Box sx={{ p: 3 /* ... */ }}>
          {/* Título con botones y buscador */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              pb: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h5"
              sx={
                {
                  /* ... */
                }
              }
            >
              Lista de Usuarios
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            {/* Buscador de usuarios */}
            <TextField
              variant="outlined"
              size="small"
              placeholder="Buscar por nombre o email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                mr: 2,
                width: 250,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Buscador de usuarios*/}

            {/* Botón de Editar */}
            <Button
              variant="contained"
              color="warning"
              startIcon={<EditIcon />}
              onClick={() =>
                selectedUser && handleOpenEditModal(selectedUser)
              }
              disabled={!selectedUser}
              sx={{
                textTransform: "none",
                fontFamily: '"Titillium Web", sans-serif',
                fontWeight: 600,
                mr: 2,
              }}
            >
              Editar Usuario
            </Button>

            {/* Botón de Eliminar*/}
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() =>
                selectedUser && handleOpenDeleteModal(selectedUser)
              }
              disabled={!selectedUser}
              sx={{
                textTransform: "none",
                fontFamily: '"Titillium Web", sans-serif',
                fontWeight: 600,
                mr: 2,
              }}
            >
              Eliminar Usuario
            </Button>

            {/* Botón de Agregar */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              sx={{
                textTransform: "none",
                fontFamily: '"Titillium Web", sans-serif',
                fontWeight: 600,
              }}
            >
              Nuevo Usuario
            </Button>
          </Box>

          {/* Modal para confirmar eliminación */}
          <Dialog
            open={deleteModalOpen}
            onClose={handleCloseDeleteModal}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Confirmar Eliminación
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                ¿Estás seguro que deseas eliminar al usuario{" "}
                {selectedUser?.name}? Esta acción no se puede deshacer.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteModal} disabled={deleteLoading}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                autoFocus
                disabled={deleteLoading}
                startIcon={<DeleteIcon />}
              >
                {deleteLoading ? "Eliminando..." : "Eliminar"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Modal para editar usuario */}
          <Modal
            open={editModalOpen}
            onClose={handleCloseEditModal}
            aria-labelledby="modal-editar-usuario"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h6"
                mb={3}
                sx={{ fontFamily: '"Titillium Web", sans-serif' }}
              >
                Editar Usuario
              </Typography>

              {/* Mostrar mensaje de error si existe */}
              {editErrorMessage && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {editErrorMessage}
                </Typography>
              )}

              <TextField
                fullWidth
                label="Nombre"
                name="name"
                value={editData.name}
                onChange={handleEditInputChange}
                margin="normal"
                sx={{ mb: 2 }}
              />

              <Typography
                variant="subtitle2"
                sx={{ mt: 2, mb: 1, color: "text.secondary" }}
              >
                Cambiar Contraseña (dejar vacío para mantener la actual)
              </Typography>

              <TextField
                fullWidth
                label="Nueva Contraseña"
                name="password"
                type="password"
                value={editData.password}
                onChange={handleEditInputChange}
                margin="normal"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Confirmar Contraseña"
                name="confirmPassword"
                type="password"
                value={editData.confirmPassword}
                onChange={handleEditInputChange}
                margin="normal"
                sx={{ mb: 3 }}
                error={
                  editData.confirmPassword !== "" &&
                  editData.password !== editData.confirmPassword
                }
                helperText={
                  editData.confirmPassword !== "" &&
                  editData.password !== editData.confirmPassword
                    ? "Las contraseñas no coinciden"
                    : ""
                }
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  onClick={handleCloseEditModal}
                  variant="outlined"
                  disabled={editLoading}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmEdit}
                  variant="contained"
                  disabled={editLoading}
                >
                  {editLoading ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </Box>
            </Box>
          </Modal>

          {/* Modal para crear usuario */}
          <Modal
            open={openModal}
            onClose={handleCloseModal}
            aria-labelledby="modal-crear-usuario"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 4,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="h6"
                mb={3}
                sx={{ fontFamily: '"Titillium Web", sans-serif' }}
              >
                Crear Nuevo Usuario
              </Typography>

              {/* Mostrar mensaje de error si existe */}
              {errorMessage && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Typography>
              )}

              <TextField
                fullWidth
                label="Nombre"
                name="name"
                value={newUser.name}
                onChange={handleInputChange}
                margin="normal"
                sx={{ mb: 2 }}
                error={!!errorMessage && errorMessage.includes("nombre")}
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={newUser.email}
                onChange={handleInputChange}
                margin="normal"
                sx={{ mb: 2 }}
                error={errorMessage?.includes("Email") || false}
                helperText={errorMessage?.includes("Email") ? errorMessage : ""}
              />

              <TextField
                fullWidth
                label="Contraseña"
                name="password"
                type="password"
                value={newUser.password}
                onChange={handleInputChange}
                margin="normal"
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  onClick={handleCloseModal}
                  variant="outlined"
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateUser}
                  variant="contained"
                  disabled={
                    !newUser.name ||
                    !newUser.email ||
                    !newUser.password ||
                    loading
                  }
                >
                  {loading ? "Creando..." : "Crear"}
                </Button>
              </Box>
            </Box>
          </Modal>

          {/* Contenedor del DataGrid */}
          <Box
            sx={{
              height: "calc(100vh - 300px)", // Ajusta esta altura según tu layout
              width: "100%",
              "& .MuiDataGrid-root": {
                height: "100%",
              },
              "& .MuiDataGrid-virtualScroller": {
                overflow: "auto",
              },
            }}
          >
            <DataGridSSR
              rows={allData}
              columns={columns}
              rowCount={meta.totalItems}
              paginationModel={{
                page: pagination.page,
                pageSize: pagination.limit,
              }}
              onPaginationModelChange={handlePaginationChange}
              loading={loading}
              onRowClick={(params) => setSelectedUser(params.row)}
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
              }}
              pageSizeOptions={[5, 10, 20]}
              paginationMode="server"
            />
          </Box>
        </Box>
      </TabstyledCard>
    </ContentTabStyleCard>
  );
};

const SettingsApp = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const isSuperAdmin = useRoleCheck(["superadmin"]);

  if (!isSuperAdmin) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 5 }}>
        Acceso denegado
      </Typography>
    );
  }

  const tabs = [
    {
      label: "Historial General",
      icon: <ListAltIcon />,
      component: <GeneralTabContent />,
    },
    {
      label: "Administrador de usuarios",
      icon: <PersonIcon />,
      component: <UserTabContent />,
    },
  ];

  const handleTabChange = (_: any, newValue: number) => {
    if (newValue < tabs.length) {
      setSelectedTab(newValue);
    }
  };

  return (
    <Grid container sx={{ height: "100%", ml: 3 }} spacing={2}>
      <Grid item xs={3}>
        <StyledCard>
          <Tabs
            orientation="vertical"
            value={selectedTab}
            onChange={handleTabChange}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        </StyledCard>
      </Grid>
      <Grid item xs={8}>
        {tabs[selectedTab]?.component}
      </Grid>
    </Grid>
  );
};

export default SettingsApp;